import React from 'react';
import { arrayOf, string, number, func, object, bool } from 'prop-types';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { LocationAutocompleteInput, Tooltip, IconInfo } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './LocationDeliveryFilter.module.css';

const identity = v => v;

const LocationSearchField = props => {
  const { intl, isMobile = false, inputRef, onLocationChange } = props;

  return (
    <Field
      name="deliveryLocation"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <div className={css.locationInput}>
            <label className={css.label} htmlFor={input.name}>
              {intl.formatMessage({ id: 'LocationDeliveryFilter.label' })}
              <Tooltip
                id="LocationDeliveryFilter.tooltip"
                className={css.tooltip}
                contentClassName={css.tooltipContent}
                text={intl.formatMessage({ id: 'LocationDeliveryFilter.tooltipText' })}
                place="right"
              >
                <IconInfo className={css.tooltipIcon} />
              </Tooltip>
            </label>
            <LocationAutocompleteInput
              inputClassName={css.locationInputRoot}
              predictionsClassName={css.predictionsRoot}
              predictionItemClassName={css.predictionItem}
              validClassName={css.validLocation}
              placeholder={intl.formatMessage({ id: 'LocationDeliveryFilter.placeholder' })}
              closeOnBlur={!isMobile}
              inputRef={inputRef}
              input={{ ...restInput, onChange: searchOnChange }}
              meta={meta}
            />
          </div>
        );
      }}
    />
  );
};

const LocationDeliveryFilterComponent = props => {
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    showAsPopup,
    initialValues,
    contentPlacementOffset,
    onSubmit,
    intl,
    isMobile,
    inputRef,
    listings,
    ...rest
  } = props;

  const hasInitialValues = !!initialValues && !!initialValues[name];
  const classes = classNames(rootClassName || css.root, className);
  const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
  const labelForPlain = <span className={labelClass}>{label}</span>;

  const handleSubmit = location => {
    if (location?.selectedPlace) {
      const { search, selectedPlace } = location;
      const { bounds, origin } = selectedPlace;

      // origin: the place's point, from which the products page keeps the
      // listings whose truck delivery radius reaches it.
      onSubmit({
        address: search,
        bounds,
        origin,
      });
    }
  };

  const handleClear = () => {
    onSubmit({
      address: null,
      bounds: null,
      origin: null,
      ids: null,
    });
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={label}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={identity}
      onClear={handleClear}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <LocationSearchField
        intl={intl}
        isMobile={isMobile}
        inputRef={inputRef}
        onLocationChange={handleSubmit}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={identity}
      onClear={handleClear}
      initialValues={initialValues}
      {...rest}
    >
      <LocationSearchField
        intl={intl}
        isMobile={isMobile}
        inputRef={inputRef}
        onLocationChange={handleSubmit}
      />
    </FilterPlain>
  );
};

LocationDeliveryFilterComponent.defaultProps = {
  rootClassName: null,
  className: null,
  showAsPopup: false,
  initialValues: null,
  contentPlacementOffset: 0,
  isMobile: false,
  inputRef: null,
};

LocationDeliveryFilterComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  label: string.isRequired,
  showAsPopup: bool,
  initialValues: object,
  contentPlacementOffset: number,
  onSubmit: func.isRequired,
  intl: object.isRequired,
  isMobile: bool,
  inputRef: object,
};

const LocationDeliveryFilter = props => {
  const { includeFor, currentPage, ...rest } = props;

  if (!includeFor.includes(currentPage)) {
    return null;
  }

  return <LocationDeliveryFilterComponent {...rest} />;
};

LocationDeliveryFilter.defaultProps = {
  includeFor: [],
};

LocationDeliveryFilter.propTypes = {
  includeFor: arrayOf(string),
  currentPage: string.isRequired,
};

export default LocationDeliveryFilter;
